import { Box, Button, Group, Stack, Switch, Text, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconLink, IconCopy, IconExternalLink } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { copyToClipboard } from '@mygpt/app/utils';
import { InfoTooltip } from '@/components/Common/InfoTooltip';
import type { RealtimeFormValues } from './index';

interface Props {
  botId: string;
  form: UseFormReturnType<RealtimeFormValues>;
  onShowRealtimeInShareChange: (value: boolean) => void;
}

export const ShareLink = ({ botId, form, onShowRealtimeInShareChange }: Props) => {
  const { t } = useTranslation();

  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  // 生成分享链接
  const shareLink = isLocalMode
    ? `${window.location.origin}/realtime/${botId}`
    : `${import.meta.env.VITE_BASE_HOST}/realtime/${botId}`;

  // 复制链接
  const handleCopyLink = () => {
    const copied = copyToClipboard(shareLink);
    if (copied) {
      showNotification({
        message: t('link_copied'),
        color: 'green',
      });
    }
  };

  // 打开 realtime 页面
  const handleOpenLink = () => {
    window.open(shareLink, '_blank');
  };

  return (
    <div
      className="relative rounded-xl bg-white flex-1 p-6"
      style={{ boxShadow: '0px 10px 60px 0px rgba(226, 236, 249, 0.50)' }}
    >
      <Stack gap="lg">
        {/* 标题和说明 */}
        <Stack gap={5}>
          <Group gap={5}>
            <Text size="sm" fw={600}>
              {t('settings.realtime.share.title')}
            </Text>
            <InfoTooltip label={t('settings.realtime.share.desc')} position="right" />
          </Group>
          <Text size="xs" c="gray.6">
            {t('settings.realtime.share.subtitle')}
          </Text>
        </Stack>

        {/* 链接展示和复制 */}
        <Box className="bg-gray-50 rounded-lg p-5">
          <Stack gap="md">
            <Group gap="xs" align="center">
              <IconLink size={20} className="text-gray-600" />
              <Text size="sm" fw={500}>
                {t('settings.realtime.share.link_label')}
              </Text>
            </Group>

            <TextInput
              value={shareLink}
              readOnly
              rightSection={
                <Button variant="subtle" size="xs" leftSection={<IconCopy size={14} />} onClick={handleCopyLink}>
                  {t('copy')}
                </Button>
              }
              rightSectionWidth={100}
              styles={{
                input: {
                  backgroundColor: 'white',
                  cursor: 'text',
                },
              }}
            />

            {/* 打开链接按钮 */}
            <Group justify="flex-end" mt="sm">
              <Button variant="light" leftSection={<IconExternalLink size={16} />} onClick={handleOpenLink}>
                {t('settings.realtime.share.open_link')}
              </Button>
            </Group>
          </Stack>
        </Box>

        {/* 分享页是否显示 realtime 入口按钮（默认关闭）*/}
        <Box className="bg-gray-50 rounded-lg p-5">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={4} className="flex-1">
              <Text size="sm" fw={600}>
                {t('settings.realtime.share.show_in_share_title')}
              </Text>
              <Text size="xs" c="gray.6">
                {t('settings.realtime.share.show_in_share_desc')}
              </Text>
            </Stack>
            <Switch
              checked={form.values.show_realtime_in_share}
              onChange={(e) => onShowRealtimeInShareChange(e.currentTarget.checked)}
            />
          </Group>
        </Box>
      </Stack>
    </div>
  );
};
